import React, { useState } from "react";
import { Box, Button, Stack, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import BottomSheet from "../../../common/BottomSheet";
import { DATE_RANGE_PRESETS, DateRangePreset } from "../../../../types/reports";
import { ViewRange } from "../../../../types/reportsV2";
import { FIXED_DATES_PRESET } from "../../v2/model/viewConfig";
import { ResolvedRange, resolvedHint } from "../../v2/ReportDateChip";
import SheetOption from "./SheetOption";
import styles from "./MobileSheets.module.css";

export interface MobileDateSheetProps {
  opened: boolean;
  onClose: () => void;
  range: ViewRange;
  /** The definition's presets. */
  presets: DateRangePreset[];
  /** Resolved dates of the last run (the selected preset's hint). */
  resolved?: ResolvedRange | null;
  onChange: (range: ViewRange) => void;
}

type BodyProps = Omit<MobileDateSheetProps, "opened">;

const presetLabel = (preset: DateRangePreset) =>
  DATE_RANGE_PRESETS.find((p) => p.value === preset)?.label ?? preset;

/** Mounted per open, so the custom dates always start from the view's
 *  range: a half-picked range never survives closing the sheet. */
function DateSheetBody({
  onClose,
  range,
  presets,
  resolved,
  onChange,
}: BodyProps) {
  const [start, setStart] = useState<string | null>(range.start ?? null);
  const [end, setEnd] = useState<string | null>(range.end ?? null);
  const ready = !!start && !!end && start <= end;
  const hint = resolvedHint(range, resolved);

  const apply = () => {
    if (!start || !end || start > end) return;
    onChange({ preset: FIXED_DATES_PRESET, start, end });
    onClose();
  };

  return (
    <>
      <Box role="radiogroup" aria-label="Date range" className={styles.options}>
        {presets
          .filter((p) => p !== FIXED_DATES_PRESET)
          .map((preset) => (
            <SheetOption
              key={preset}
              checked={range.preset === preset}
              label={presetLabel(preset)}
              sub={range.preset === preset ? hint : undefined}
              onClick={() => {
                onChange({ preset });
                onClose();
              }}
            />
          ))}
      </Box>
      <Text className={styles.sectionLabel}>Custom range</Text>
      <Stack gap="sm">
        <DatePickerInput
          label="Start"
          placeholder="Pick start"
          dropdownType="modal"
          value={start}
          onChange={(value) => setStart(value ?? null)}
          maxDate={end ?? undefined}
        />
        <DatePickerInput
          label="End"
          placeholder="Pick end"
          dropdownType="modal"
          value={end}
          onChange={(value) => setEnd(value ?? null)}
          minDate={start ?? undefined}
        />
        {!ready && (
          <Text className={styles.hint}>
            Pick both a start and end date to run the report.
          </Text>
        )}
        <Button color="teal" disabled={!ready} onClick={apply}>
          Apply range
        </Button>
      </Stack>
    </>
  );
}

/**
 * "Date range" on a phone: the definition's presets (a tap applies and
 * closes), then a custom range that only applies once both dates are picked
 * — the desktop date chip's rule, so a half-picked range never runs.
 */
export default function MobileDateSheet({
  opened,
  ...body
}: MobileDateSheetProps) {
  return (
    <BottomSheet
      labelDialog
      opened={opened}
      onClose={body.onClose}
      title="Date range"
      data-testid="report-date-sheet"
    >
      {opened && <DateSheetBody {...body} />}
    </BottomSheet>
  );
}
